import { API_CONSTANTS, AUTH_CONSTANTS } from "@/constants";

export interface ApiVersionResponse {
  version: string;
  imageTag: string;
  buildTime: string;
  environment: string;
}

const FETCH_TIMEOUT_MS = 5000;

/**
 * Caches API version response in session storage
 */
const cacheApiVersion = (data: ApiVersionResponse): void => {
  try {
    sessionStorage.setItem(AUTH_CONSTANTS.STORAGE_KEYS.API_VERSION, JSON.stringify(data));
  } catch (error) {
    console.warn("[ApiVersion] Failed to cache version:", error);
  }
};

/**
 * Gets cached API version from session storage
 */
export const getCachedApiVersion = (): ApiVersionResponse | null => {
  try {
    const cached = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.API_VERSION);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.warn("[ApiVersion] Failed to read cached version:", error);
  }
  return null;
};

/**
 * Fetches API version from the /version endpoint.
 * This endpoint is unauthenticated — uses direct fetch() with no auth headers,
 * following the partnerResolution.ts pattern.
 *
 * Returns null on failure (network error, timeout, non-OK status).
 */
export const fetchApiVersion = async (): Promise<ApiVersionResponse | null> => {
  const apiUrl = import.meta.env.VITE_API_SERVER_URL || "http://localhost:9999";
  const url = `${apiUrl}${API_CONSTANTS.ENDPOINTS.VERSION}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      console.warn(`[ApiVersion] Version endpoint returned ${response.status}`);
      return null;
    }

    const data: ApiVersionResponse = await response.json();
    cacheApiVersion(data);
    return data;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      console.warn("[ApiVersion] Version fetch timed out");
    } else {
      console.warn("[ApiVersion] Failed to fetch version:", error);
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};
